import { useState } from 'react';
import axios from 'axios';
import { Phone, Mail, MapPin, Clock, Send, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CONTACT } from '../data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll be in touch shortly.");
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error('Could not send message. Please try again or call us.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24 bg-[#0a0a0a] text-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <span className="pill mb-4">Contact</span>
        <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter max-w-3xl">Let's get your detail on the schedule.</h1>

        <div className="grid lg:grid-cols-5 gap-10 mt-16">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#141414] border border-white/5 rounded-2xl p-6">
              <Phone className="w-5 h-5 text-blue-400 mb-3" />
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Phone</div>
              <a href={`tel:${CONTACT.phoneRaw}`} className="font-display text-xl hover:text-blue-400 transition-colors">{CONTACT.phone}</a>
            </div>
            <div className="bg-[#141414] border border-white/5 rounded-2xl p-6">
              <Mail className="w-5 h-5 text-blue-400 mb-3" />
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Email</div>
              <a href={`mailto:${CONTACT.email}`} className="font-display text-xl hover:text-blue-400 transition-colors break-all">{CONTACT.email}</a>
            </div>
            <div className="bg-[#141414] border border-white/5 rounded-2xl p-6">
              <MapPin className="w-5 h-5 text-blue-400 mb-3" />
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Service Area</div>
              <div className="font-display text-xl">{CONTACT.address}</div>
              <div className="text-zinc-400 text-sm mt-1">+ Surrounding communities</div>
            </div>
            <div className="bg-[#141414] border border-white/5 rounded-2xl p-6">
              <Clock className="w-5 h-5 text-blue-400 mb-3" />
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Business Hours</div>
              <ul className="space-y-2 text-sm">
                {CONTACT.hours.map((h) => (
                  <li key={h.day} className="flex justify-between">
                    <span className="text-zinc-400">{h.day}</span>
                    <span>{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Link to="/book" data-testid="contact-book-button" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full font-medium transition-all hover:shadow-[0_0_40px_rgba(37,99,235,0.5)] inline-flex items-center gap-2">
              Book Appointment <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-3">
            <form onSubmit={submit} data-testid="contact-form" className="bg-[#141414] border border-white/5 rounded-2xl p-8 space-y-5">
              <h2 className="font-display text-2xl mb-2">Send us a message</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name *" data-testid="contact-name" className="bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500 transition-colors" />
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" data-testid="contact-email" className="bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500 transition-colors" />
              </div>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" data-testid="contact-phone" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500 transition-colors" />
              <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your vehicle and what you need *" data-testid="contact-message" className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-blue-500 transition-colors resize-none" />
              <button disabled={submitting} data-testid="contact-submit" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-full font-medium transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.4)] inline-flex items-center gap-2 disabled:opacity-50">
                {submitting ? 'Sending…' : (<>Send Message <Send className="w-4 h-4" /></>)}
              </button>
            </form>

            <div className="mt-6 rounded-2xl overflow-hidden border border-white/5 h-64">
              <iframe
                title="Lincoln, NE map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-96.8082%2C40.7494%2C-96.6082%2C40.8694&layer=mapnik&marker=40.8094%2C-96.6802"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
